import ReactGA from 'react-ga4';

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-W47WWVZGL4';

export const initGA = () => {
  if (GA_MEASUREMENT_ID) {
    ReactGA.initialize(GA_MEASUREMENT_ID, {
      gtagOptions: {
        send_page_view: false,
      },
    });
  } else {
    console.warn('Google Analytics Measurement ID not found');
  }
};

/**
 * Collapse high-cardinality query strings so GA reports stay usable.
 * /web keeps q + view; /desk keeps q + mode; admin/oauth paths drop query.
 */
export function analyticsPath(pathname, search = '') {
  const path = pathname || '/';
  if (path.startsWith('/admin') || path.startsWith('/settings')) {
    return path.split('/').slice(0, 2).join('/') || path;
  }
  if (path.startsWith('/login/oauth2')) {
    return '/login/oauth2';
  }
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const keep = new URLSearchParams();
  if (path === '/web') {
    ['q', 'view', 'layout'].forEach((key) => {
      const value = params.get(key);
      if (value) {
        keep.set(key, value);
      }
    });
  } else if (path === '/desk') {
    ['q', 'mode'].forEach((key) => {
      const value = params.get(key);
      if (value) {
        keep.set(key, value);
      }
    });
  }
  const qs = keep.toString();
  return qs ? `${path}?${qs}` : path;
}

export const trackPageView = (path) => {
  if (GA_MEASUREMENT_ID) {
    ReactGA.send({ hitType: 'pageview', page: path });
  }
};

export const trackEvent = (action, category, label, value) => {
  if (GA_MEASUREMENT_ID) {
    ReactGA.event({
      action,
      category,
      label,
      value,
    });
  }
};

export const trackArticleView = (articleId, articleTitle, source) => {
  trackEvent('article_view', 'content', articleId, 1);
  trackEvent('article_view', 'source', source, 1);

  ReactGA.event({
    action: 'view_item',
    category: 'engagement',
    label: articleTitle,
    custom_parameters: {
      article_id: articleId,
      source,
      content_type: 'article',
    },
  });
};

export const trackSearch = (searchTerm, resultsCount, surface = 'feed') => {
  trackEvent('search', surface, searchTerm, resultsCount);
};

export const trackGraphView = (viewMode, query) => {
  trackEvent('graph_view', 'web', `${viewMode || 'entity'}:${query || ''}`, 1);
};

export const trackAuth = (action, method) => {
  trackEvent('auth', 'user', `${action}_${method}`, 1);
};

export const trackSocialShare = (platform, articleId) => {
  trackEvent('share', 'social', platform, 1);
  trackEvent('share', 'content', articleId, 1);
};

export const trackReadingTime = (articleId, timeSpent) => {
  trackEvent('reading_time', 'engagement', articleId, timeSpent);
};

export const trackScrollDepth = (articleId, depth) => {
  trackEvent('scroll_depth', 'engagement', articleId, depth);
};

export const trackArticleClick = (articleId, articleTitle, source) => {
  trackEvent('article_click', 'feed', articleId, 1);
  trackEvent('article_click', 'source', source, 1);
};

export const trackLandingCtaClick = (label) => {
  trackEvent('landing_cta_click', 'landing', label, 1);
};

const analytics = {
  initGA,
  analyticsPath,
  trackPageView,
  trackEvent,
  trackArticleView,
  trackSearch,
  trackGraphView,
  trackAuth,
  trackSocialShare,
  trackReadingTime,
  trackScrollDepth,
  trackArticleClick,
  trackLandingCtaClick,
};

export default analytics;
